import CustomModal from '../../../modal/CustomModal'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../../../buttons/Button'
import { useForm } from 'react-hook-form'
import { InvoiceCustomerDataDescription, InvoiceCustomerDataSubtitle, InvoiceCustomerDataWrapper } from './index.styled'
import { useDispatch, useSelector } from 'react-redux'
import MainCustomerDataForm from './main'
import { removeEmptyFields } from '../../../../../helper'
import { setCustomer, setCustomerAddressIsNotFull, setCustomersByCompanyName, setCustomersByEmail, setCustomersByName, setCustomersByNumber, setCustomersByPhone } from '../../../../../redux/action/customer'
import { resetCustomerThunk, searchResetThunk, storeCustomerThunk, updateCustomerBillingAddressThunk, updateCustomerEmailThunk } from '../../../../../redux/thunk/customer'
import { logger } from '../../../../../logger/logger'
import { emptyBillingAddressValues, toBillingAddress, toCustomerFormValues, toPaidOrderRecipient, toRecipientAddress } from './helper'
import { selectCustomer } from '../../../../../redux/selector/customer'

const EVENT_Z_INDEX = 72

const defaultValues = {
	phoneNumber: null, customerNumber: null, name: null, companyName: null, address: null, streetNumber: null,
	street: null, city: null, postalCode: null, remark: null, deliveryCost: 0, customerAddress: null, email: null,
	billingAddress: emptyBillingAddressValues,
}

const customerSuggestionsActions = [setCustomersByPhone, setCustomersByNumber, setCustomersByName, setCustomersByEmail, setCustomersByCompanyName]

const InvoiceCustomerDataModal = React.memo(({ isOpen, close, onSubmit, captureCustomerData = false, paidOrderMode = false, customer: orderCustomer = null, returnZIndex = 0 }) => {
	const { t } = useTranslation()
	const { handleSubmit, register, formState: { errors }, reset, setValue, watch, setFocus, clearErrors } = useForm({ defaultValues })
	const dispatch = useDispatch()
	const cartCustomer = useSelector(selectCustomer)
	const [pickedCustomer, setPickedCustomer] = useState(null)
	const customer = paidOrderMode ? (pickedCustomer ?? orderCustomer) : cartCustomer
	const [onClickIsProcessing, setOnClickIsProcessing] = useState(false)
	const [isBillingAddressSeparate, setIsBillingAddressSeparate] = useState(false)
	const [isFirma, setIsFirma] = useState(true)
	const isButtonsDisabled = !isOpen || onClickIsProcessing

	const handleReset = useCallback((values) => {
		reset({ ...defaultValues, ...values })
	}, [reset])

	const isOrderFirmaOffByDefault = !!customer && !customer.companyName && !!customer.name

	useEffect(() => {
		if (paidOrderMode) setIsFirma(!(isOpen && isOrderFirmaOffByDefault))
	}, [paidOrderMode, isOpen, isOrderFirmaOffByDefault])

	useEffect(() => {
		if (paidOrderMode) return
		if (isOpen && customer && !customer?.companyName) {
			setIsFirma(false)
		} else {
			setIsFirma(true)
		}
	}, [isOpen, customer?.companyName]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		setIsBillingAddressSeparate(isOpen && !!customer?.billingAddress)
		if (isOpen && customer) {
			handleReset(toCustomerFormValues(customer))
			if (paidOrderMode) return

			if (!customer.city || !customer.street || !customer.postalCode) {
				dispatch(setCustomerAddressIsNotFull(true))
			} else {
				dispatch(setCustomerAddressIsNotFull(false))
			}
		}
	}, [isOpen, customer]) // eslint-disable-line react-hooks/exhaustive-deps

	const restoreOrderCustomer = useCallback(() => {
		setPickedCustomer(null)
		setIsBillingAddressSeparate(!!orderCustomer?.billingAddress)
		handleReset(orderCustomer ? toCustomerFormValues(orderCustomer) : defaultValues)
	}, [handleReset, orderCustomer])

	const handleEscape = useCallback(() => {
		if (paidOrderMode) {
			restoreOrderCustomer()
			return
		}
		handleReset(defaultValues)
		dispatch(resetCustomerThunk())
		dispatch(searchResetThunk())
	}, [handleReset, restoreOrderCustomer, dispatch, paidOrderMode])

	const handleResetBack = useCallback(() => {
		if (paidOrderMode) {
			setPickedCustomer(null)
			handleReset(defaultValues)
			return
		}
		handleReset(captureCustomerData ? customer : defaultValues)
		if (!captureCustomerData) dispatch(resetCustomerThunk())
		dispatch(searchResetThunk())
	}, [handleReset, dispatch, paidOrderMode, captureCustomerData, customer])

	const selectCustomerSuggestion = useCallback((data) => {
		if (paidOrderMode) {
			setPickedCustomer(data)
			customerSuggestionsActions.forEach(action => dispatch(action(null)))
			return
		}
		dispatch(setCustomer(data))
		dispatch(searchResetThunk())
	}, [dispatch, paidOrderMode])

	const sendRecipient = useCallback(async (data, customerNumber = null) => {
		const dataValue = customerNumber ? { ...data, customerNumber } : data
		setOnClickIsProcessing(true)
		try {
			await onSubmit(dataValue)
			handleEscape()
		} catch (error) {
			logger.error('Send invoice failed: ', error)
		} finally {
			setOnClickIsProcessing(false)
		}
	}, [onSubmit, handleEscape])

	const saveBillingAddress = useCallback(async (customerNumber, billingAddress) => {
		if (!billingAddress) return true
		return !!(await dispatch(updateCustomerBillingAddressThunk(customerNumber, billingAddress)))
	}, [dispatch])

	const submitPaidOrderRecipient = useCallback(async (data) => {
		const customerNumber = customer?.customerNumber ?? null
		const billingAddress = isBillingAddressSeparate && customerNumber ? toBillingAddress(data.billingAddress) : null
		if (!await saveBillingAddress(customerNumber, billingAddress)) return
		await sendRecipient(toPaidOrderRecipient({ ...data, customerNumber }, billingAddress, isFirma))
	}, [customer, isBillingAddressSeparate, isFirma, saveBillingAddress, sendRecipient])

	const submitCartRecipient = useCallback(async (data) => {
		const { billingAddress: billingAddressValues, ...recipientValues } = data
		const billingAddress = isBillingAddressSeparate && recipientValues.customerNumber ? toBillingAddress(billingAddressValues) : null
		const dataValue = removeEmptyFields({
			...recipientValues,
			companyName: isFirma ? recipientValues.companyName : null,
			...toRecipientAddress(data.customerAddress),
			geoData: null,
			billingAddress,
		})
		if (dataValue.customerNumber) {
			if (!await saveBillingAddress(dataValue.customerNumber, billingAddress)) return
			await dispatch(updateCustomerEmailThunk(dataValue.customerNumber, { email: dataValue.email }))
			await sendRecipient(dataValue)
		} else {
			const customerNumber = await dispatch(storeCustomerThunk(dataValue))
			if (!customerNumber) return
			await sendRecipient(dataValue, customerNumber)
		}
	}, [isBillingAddressSeparate, isFirma, saveBillingAddress, sendRecipient, dispatch])

	const submitRecipient = paidOrderMode ? submitPaidOrderRecipient : submitCartRecipient

	const toggleFirma = useCallback(() => {
		setIsFirma(prevState => !prevState)
	}, [])

	const toggleBillingAddressSeparate = useCallback(() => {
		setIsBillingAddressSeparate(prevState => !prevState)
	}, [])

	const handleClose = useCallback(() => {
		handleResetBack()
		close()
	}, [handleResetBack, close])

	return (
		<CustomModal isOpen={isOpen}
								 close={handleClose}
								 title={t('InvoiceModals.customerData.title')}
								 size="x-small"
								 id="invoice-customer-data-modal"
								 zIndex={EVENT_Z_INDEX}
								 returnZIndex={returnZIndex}
								 disableBackButton={true}
								 showCloseButton={!isButtonsDisabled}
								 enableOutsideClick={!isButtonsDisabled}
								 button={
									 <>
										 <Button name="cancel" icon="clear" text={t('buttons.controls.cancel_1')}
														 keyButton="F3" zIndex={EVENT_Z_INDEX}
														 onClick={handleClose}
														 className="back"
														 disabled={isButtonsDisabled} />
										 <Button name="send-email" color="green" icon="send-email" text={t('buttons.controls.send_invoice')}
														 keyButton="F2" zIndex={EVENT_Z_INDEX}
														 onClick={handleSubmit(submitRecipient)}
														 disabled={isButtonsDisabled} />
									 </>
								 }>
			<InvoiceCustomerDataWrapper>
				<InvoiceCustomerDataSubtitle>{t('InvoiceModals.customerData.subtitle')}</InvoiceCustomerDataSubtitle>
				{isOpen &&
					<MainCustomerDataForm {...{ handleSubmit, submitRecipient, register, errors, reset, setValue, setFocus, watch, zIndex: EVENT_Z_INDEX, handleEscape, clearErrors, captureCustomerData, paidOrderMode, isBillingAddressSeparate, toggleBillingAddressSeparate, customer, selectCustomerSuggestion, isFirma, toggleFirma }} />}
				<InvoiceCustomerDataDescription>{'*' + t('InvoiceModals.description')}</InvoiceCustomerDataDescription>
			</InvoiceCustomerDataWrapper>
		</CustomModal>
	)
})

InvoiceCustomerDataModal.displayName = 'InvoiceCustomerDataModal'
export default InvoiceCustomerDataModal
